import React, { useCallback, useState, useEffect } from "react";
import { faGripDotsVertical } from "@fortawesome/pro-solid-svg-icons";
import {
  faTrash,
  faEdit,
  faSave,
  faTimes,
  faPlus,
  faExclamationTriangle,
} from "@fortawesome/pro-regular-svg-icons";
import Button from "../../../buttons/Button";
import Alert from "../../../utils/Alert";
import Tag from "../../../utils/Tag";
import Collapse from "../../../utils/Collapse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getVariantGroupsByRestaurant,
  createVariantGroup,
  updateVariantGroup,
  deleteVariantGroup,
  updateVariantGroupsOrder,
  addVariantToGroup,
  updateVariant,
  deleteVariant,
  updateVariantsOrder,
} from "../../../../services/dishVariantGroupService";
import { toast } from "../../../../utils/demoToast";
import { useSelector } from "react-redux";

// ─── Sortable row wrapper (reused for groups and variants) ───────────────────

const SortableRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return children({ setNodeRef, style, attributes, listeners, isDragging });
};

// ─── Variant row (single variant inside a group) ────────────────────────────

const VariantRow = ({
  variant,
  tKey,
  editVariantId,
  editVariantData,
  setEditVariantId,
  setEditVariantData,
  deleteConfirmVariantId,
  setDeleteConfirmVariantId,
  onUpdate,
  onDelete,
}) => {
  const { t } = useTranslation();

  const isEditing = editVariantId === variant.dishVariantId;

  return (
    <SortableRow id={String(variant.dishVariantId)}>
      {({ setNodeRef, style, attributes, listeners }) => (
        <div
          ref={setNodeRef}
          style={{ ...style, borderBottom: "1px solid #eee" }}
          className="flex justify-between align-center px-3 py-2"
        >
          <div className="flex align-center gap-1">
            <span {...attributes} {...listeners} className="cursor-move">
              <FontAwesomeIcon icon={faGripDotsVertical} size="sm" />
            </span>

            {isEditing ? (
              <div className="flex gap-1 align-center" style={{ flex: 1 }}>
                <input
                  type="text"
                  value={editVariantData.label}
                  onChange={(e) =>
                    setEditVariantData((prev) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                  className="bg-pastel-light"
                  style={{ flex: 3 }}
                  placeholder={t(`${tKey}variantsManagement.variantLabel`)}
                />
                <input
                  type="number"
                  step="0.05"
                  value={editVariantData.priceImpact}
                  onChange={(e) =>
                    setEditVariantData((prev) => ({
                      ...prev,
                      priceImpact: e.target.value,
                    }))
                  }
                  className="bg-pastel-light"
                  style={{ flex: 1, minWidth: "80px" }}
                  placeholder={t(`${tKey}variantsManagement.priceImpact`)}
                />
              </div>
            ) : (
              <div className="flex align-center gap-1">
                <span>{variant.label}</span>
                {parseFloat(variant.priceImpact) !== 0 && (
                  <Tag
                    text={`${parseFloat(variant.priceImpact) > 0 ? "+" : ""}${parseFloat(variant.priceImpact).toFixed(2)}`}
                    variant={
                      parseFloat(variant.priceImpact) > 0 ? "danger" : "success"
                    }
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex align-center gap-1">
            {isEditing ? (
              <>
                <Button
                  icon
                  variant="success"
                  onClick={() => onUpdate(variant.dishVariantId)}
                >
                  <FontAwesomeIcon icon={faSave} />
                </Button>
                <Button
                  icon
                  variant="secondary"
                  onClick={() => setEditVariantId(null)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </>
            ) : (
              <Button
                icon
                variant="info"
                onClick={() => {
                  setEditVariantId(variant.dishVariantId);
                  setEditVariantData({
                    label: variant.label,
                    priceImpact: variant.priceImpact,
                  });
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            )}

            {deleteConfirmVariantId === variant.dishVariantId ? (
              <Button
                variant="danger"
                size="xs"
                onClick={() => {
                  onDelete(variant.dishVariantId);
                  setDeleteConfirmVariantId(null);
                }}
              >
                {t(`${tKey}variantsManagement.deleteVariantConfirm`)}
              </Button>
            ) : (
              <Button
                icon
                variant="danger"
                onClick={() => setDeleteConfirmVariantId(variant.dishVariantId)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </div>
        </div>
      )}
    </SortableRow>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const VariantGroupManager = ({
  dishId = null,
  initialAssignedGroupIds = [],
  tKey = "",
  onAssignmentChange = () => {},
  onGroupsChange = () => {},
}) => {
  const { t } = useTranslation();
  const restaurantId = useSelector((state) => state.user.restaurantId);

  // ── State ────────────────────────────────────────────────────────────────

  const [groups, setGroups] = useState([]);
  const [, setAssignedGroupIds] = useState(new Set(initialAssignedGroupIds));
  const [loading, setLoading] = useState(true);

  // Group editing
  const [editGroupId, setEditGroupId] = useState(null);
  const [editGroupData, setEditGroupData] = useState({});
  const [deleteConfirmGroupId, setDeleteConfirmGroupId] = useState(null);

  // New group form
  const [newGroupLabel, setNewGroupLabel] = useState("");

  // Variant editing (per group)
  const [editVariantId, setEditVariantId] = useState(null);
  const [editVariantData, setEditVariantData] = useState({});
  const [deleteConfirmVariantId, setDeleteConfirmVariantId] = useState(null);

  // New variant form (keyed by groupId)
  const [newVariantForms, setNewVariantForms] = useState({});

  // Expanded group
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  // ── Sensors for DnD ──────────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // ── Fetch groups on mount ────────────────────────────────────────────────

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getVariantGroupsByRestaurant(restaurantId);
        setGroups((res.data || []).sort((a, b) => a.order - b.order));
      } catch (error) {
        console.error("Error fetching variant groups:", error);
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [restaurantId]);

  useEffect(() => {
    if (!loading) {
      onGroupsChange(groups);
    }
  }, [groups, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Group CRUD ───────────────────────────────────────────────────────────

  const handleCreateGroup = useCallback(async () => {
    if (!newGroupLabel.trim()) return;
    try {
      const res = await createVariantGroup({
        label: newGroupLabel.trim(),
        restaurantId,
      });
      setGroups((prev) => [...prev, res.data]);
      setNewGroupLabel("");
      toast.success(t(`${tKey}variantsManagement.createGroupSuccess`));
    } catch (error) {
      console.error("Error creating variant group:", error);
      toast.error(error.message);
    }
  }, [newGroupLabel, restaurantId, t, tKey]);

  const handleUpdateGroup = useCallback(
    async (groupId) => {
      try {
        const res = await updateVariantGroup(groupId, editGroupData);
        setGroups((prev) =>
          prev.map((g) =>
            g.dishVariantGroupId === groupId ? { ...g, ...res.data } : g,
          ),
        );
        setEditGroupId(null);
        setEditGroupData({});
        toast.success(t(`${tKey}variantsManagement.updateGroupSuccess`));
      } catch (error) {
        console.error("Error updating variant group:", error);
        toast.error(error.message);
      }
    },
    [editGroupData, t, tKey],
  );

  const handleDeleteGroup = useCallback(
    async (groupId) => {
      try {
        await deleteVariantGroup(groupId);
        setGroups((prev) =>
          prev.filter((g) => g.dishVariantGroupId !== groupId),
        );
        // Also remove from assigned if needed
        setAssignedGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });
        toast.success(t(`${tKey}variantsManagement.deleteGroupSuccess`));
      } catch (error) {
        console.error("Error deleting variant group:", error);
        toast.error(error.message);
      }
    },
    [t, tKey],
  );

  const handleDragEndGroups = useCallback(
    async (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = groups.findIndex(
        (g) => String(g.dishVariantGroupId) === String(active.id),
      );
      const newIndex = groups.findIndex(
        (g) => String(g.dishVariantGroupId) === String(over.id),
      );
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const reordered = Array.from(groups);
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      setGroups(reordered);

      try {
        await updateVariantGroupsOrder(
          restaurantId,
          reordered.map((g) => g.dishVariantGroupId),
        );
        toast.success(t(`${tKey}variantsManagement.updateGroupOrderSuccess`));
      } catch (error) {
        console.error("Error reordering variant groups:", error);
        toast.error(error.message);
      }
    },
    [groups, restaurantId, t, tKey],
  );

  // ── Variant CRUD ─────────────────────────────────────────────────────────

  const handleAddVariant = useCallback(
    async (groupId) => {
      const form = newVariantForms[groupId] || {};
      if (!form.label?.trim()) return;
      try {
        const res = await addVariantToGroup(groupId, {
          label: form.label.trim(),
          priceImpact: parseFloat(form.priceImpact) || 0,
        });
        setGroups((prev) =>
          prev.map((g) => {
            if (g.dishVariantGroupId !== groupId) return g;
            const variants = g.dishvariants || g.variants || [];
            return { ...g, dishvariants: [...variants, res.data] };
          }),
        );
        setNewVariantForms((prev) => ({
          ...prev,
          [groupId]: { label: "", priceImpact: "" },
        }));
        toast.success(t(`${tKey}variantsManagement.createVariantSuccess`));
      } catch (error) {
        console.error("Error adding variant:", error);
        toast.error(error.message);
      }
    },
    [newVariantForms, t, tKey],
  );

  const handleUpdateVariant = useCallback(
    async (variantId) => {
      try {
        const res = await updateVariant(variantId, {
          label: editVariantData.label,
          priceImpact: parseFloat(editVariantData.priceImpact) || 0,
        });
        setGroups((prev) =>
          prev.map((g) => {
            const variants = g.dishvariants || g.variants || [];
            return {
              ...g,
              dishvariants: variants.map((v) =>
                v.dishVariantId === variantId ? { ...v, ...res.data } : v,
              ),
            };
          }),
        );
        setEditVariantId(null);
        setEditVariantData({});
        toast.success(t(`${tKey}variantsManagement.updateVariantSuccess`));
      } catch (error) {
        console.error("Error updating variant:", error);
        toast.error(error.message);
      }
    },
    [editVariantData, t, tKey],
  );

  const handleDeleteVariant = useCallback(
    async (variantId) => {
      try {
        await deleteVariant(variantId);
        setGroups((prev) =>
          prev.map((g) => {
            const variants = g.dishvariants || g.variants || [];
            return {
              ...g,
              dishvariants: variants.filter(
                (v) => v.dishVariantId !== variantId,
              ),
            };
          }),
        );
        toast.success(t(`${tKey}variantsManagement.deleteVariantSuccess`));
      } catch (error) {
        console.error("Error deleting variant:", error);
        toast.error(error.message);
      }
    },
    [t, tKey],
  );

  const handleDragEndVariants = useCallback(
    async (event, groupId) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const group = groups.find((g) => g.dishVariantGroupId === groupId);
      if (!group) return;

      const variants = group.dishvariants || group.variants || [];
      const oldIndex = variants.findIndex(
        (v) => String(v.dishVariantId) === String(active.id),
      );
      const newIndex = variants.findIndex(
        (v) => String(v.dishVariantId) === String(over.id),
      );
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const reordered = Array.from(variants);
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      setGroups((prev) =>
        prev.map((g) =>
          g.dishVariantGroupId === groupId
            ? { ...g, dishvariants: reordered }
            : g,
        ),
      );

      try {
        await updateVariantsOrder(
          groupId,
          reordered.map((v) => v.dishVariantId),
        );
        toast.success(t(`${tKey}variantsManagement.updateVariantOrderSuccess`));
      } catch (error) {
        console.error("Error reordering variants:", error);
        toast.error(error.message);
      }
    },
    [groups, t, tKey],
  );

  // ── Toggle allowMultiple directly from title ─────────────────────────────

  const handleToggleAllowMultiple = useCallback(
    async (groupId, allowMultiple) => {
      const group = groups.find((g) => g.dishVariantGroupId === groupId);
      if (!group || group.allowMultiple === allowMultiple) return;
      try {
        await updateVariantGroup(groupId, { allowMultiple });
        setGroups((prev) =>
          prev.map((g) =>
            g.dishVariantGroupId === groupId ? { ...g, allowMultiple } : g,
          ),
        );
        toast.success(t(`${tKey}variantsManagement.updateGroupSuccess`));
      } catch (error) {
        console.error("Error toggling allowMultiple:", error);
        toast.error(error.message);
      }
    },
    [groups, t, tKey],
  );

  // ── Render helpers ───────────────────────────────────────────────────────

  const getVariants = (group) => {
    return (group.dishvariants || group.variants || []).sort(
      (a, b) => a.order - b.order,
    );
  };

  const getNewVariantForm = (groupId) => {
    return newVariantForms[groupId] || { label: "", priceImpact: "" };
  };

  const updateNewVariantForm = (groupId, field, value) => {
    setNewVariantForms((prev) => ({
      ...prev,
      [groupId]: { ...getNewVariantForm(groupId), [field]: value },
    }));
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) return null;

  const groupIds = groups.map((g) => String(g.dishVariantGroupId));

  return (
    <>
      <h3 className="mt-0 mb-2">{t(`${tKey}variantsManagement.title`)}</h3>
      <Alert variant="pastel-info" className="flex gap-2 mb-3 align-center">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <p className="italic m-0" style={{ fontSize: "0.875rem" }}>
          {t(`${tKey}variantsManagement.description`)}
        </p>
      </Alert>

      {/* New group form */}
      <div className="flex gap-1 mb-3 align-center">
        <input
          type="text"
          value={newGroupLabel}
          onChange={(e) => setNewGroupLabel(e.target.value)}
          className="bg-pastel-light flex-grow"
          placeholder={t(`${tKey}variantsManagement.groupLabel`)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateGroup();
          }}
        />
        <Button
          variant="primary"
          size="sm"
          onClick={handleCreateGroup}
          disabled={!newGroupLabel.trim()}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          {t(`${tKey}variantsManagement.addGroup`)}
        </Button>
      </div>

      {groups.length === 0 ? (
        <p className="text-medium italic">
          {t(`${tKey}variantsManagement.noGroups`)}
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEndGroups}
        >
          <SortableContext
            items={groupIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex column gap-2">
              {groups.map((group) => {
                const variants = getVariants(group);
                const variantIds = variants.map((v) => String(v.dishVariantId));
                const isEditingGroup = editGroupId === group.dishVariantGroupId;
                const newVForm = getNewVariantForm(group.dishVariantGroupId);

                return (
                  <SortableRow
                    key={group.dishVariantGroupId}
                    id={String(group.dishVariantGroupId)}
                  >
                    {({ setNodeRef, style, attributes, listeners }) => (
                      <div ref={setNodeRef} style={style}>
                        <Collapse
                          isDroppable
                          dragHandleProps={{ ...attributes, ...listeners }}
                          variant="pastel-light"
                          hideCaret
                          isOpen={expandedGroupId === group.dishVariantGroupId}
                          onToggle={() =>
                            setExpandedGroupId(
                              expandedGroupId === group.dishVariantGroupId
                                ? null
                                : group.dishVariantGroupId,
                            )
                          }
                          title={
                            <div className="flex align-center gap-1 flex-grow">
                              {/* Group name: view or edit */}
                              {isEditingGroup ? (
                                <div
                                  className="flex align-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="text"
                                    value={editGroupData.label || ""}
                                    onChange={(e) =>
                                      setEditGroupData((prev) => ({
                                        ...prev,
                                        label: e.target.value,
                                      }))
                                    }
                                    className="bg-white"
                                    style={{ fontWeight: "bold" }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter")
                                        handleUpdateGroup(
                                          group.dishVariantGroupId,
                                        );
                                    }}
                                  />
                                  <Button
                                    icon
                                    variant="success"
                                    onClick={() =>
                                      handleUpdateGroup(
                                        group.dishVariantGroupId,
                                      )
                                    }
                                  >
                                    <FontAwesomeIcon icon={faSave} />
                                  </Button>
                                  <Button
                                    icon
                                    variant="primary"
                                    onClick={() => setEditGroupId(null)}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <span className="bold">{group.label}</span>
                                  {/* Variant count */}
                                  <Tag
                                    text={String(variants.length)}
                                    variant="pastel-medium"
                                  />
                                </>
                              )}
                              <div className="flex align-center gap-1 ml-auto">
                                {!isEditingGroup && (
                                  <>
                                    {/* Right-aligned controls: toggle, apply, actions */}
                                    <div
                                      className="flex align-center gap-2 ml-auto"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {/* Single / Multiple toggle */}
                                      <div
                                        style={{
                                          display: "inline-flex",
                                          backgroundColor: "white",
                                          borderRadius: "999px",
                                          padding: "2px",
                                          boxShadow:
                                            "0 1px 3px rgba(0,0,0,0.08)",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleToggleAllowMultiple(
                                              group.dishVariantGroupId,
                                              false,
                                            )
                                          }
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "0.25rem 0.6rem",
                                            border: "none",
                                            borderRadius: "999px",
                                            backgroundColor:
                                              !group.allowMultiple
                                                ? "#000"
                                                : "transparent",
                                            color: !group.allowMultiple
                                              ? "#fff"
                                              : "#999",
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {t(
                                            `${tKey}variantsManagement.singleChoice`,
                                          )}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleToggleAllowMultiple(
                                              group.dishVariantGroupId,
                                              true,
                                            )
                                          }
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "0.25rem 0.6rem",
                                            border: "none",
                                            borderRadius: "999px",
                                            backgroundColor: group.allowMultiple
                                              ? "#000"
                                              : "transparent",
                                            color: group.allowMultiple
                                              ? "#fff"
                                              : "#999",
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {t(
                                            `${tKey}variantsManagement.multipleChoice`,
                                          )}
                                        </button>
                                      </div>

                                      {/* Action buttons */}
                                      {!isEditingGroup && (
                                        <div className="flex align-center gap-1">
                                          <Button
                                            icon
                                            variant="info"
                                            onClick={() => {
                                              setEditGroupId(
                                                group.dishVariantGroupId,
                                              );
                                              setEditGroupData({
                                                label: group.label,
                                              });
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faEdit} />
                                          </Button>
                                          {deleteConfirmGroupId ===
                                          group.dishVariantGroupId ? (
                                            <Button
                                              variant="danger"
                                              size="xs"
                                              onClick={() => {
                                                handleDeleteGroup(
                                                  group.dishVariantGroupId,
                                                );
                                                setDeleteConfirmGroupId(null);
                                              }}
                                            >
                                              {t(
                                                `${tKey}variantsManagement.deleteGroupConfirm`,
                                              )}
                                            </Button>
                                          ) : (
                                            <Button
                                              icon
                                              variant="danger"
                                              onClick={() =>
                                                setDeleteConfirmGroupId(
                                                  group.dishVariantGroupId,
                                                )
                                              }
                                            >
                                              <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          }
                        >
                          {/* Single vs Multiple choice info */}
                          <Alert
                            variant="pastel-info"
                            className="mx-3 mt-2 mb-2"
                          >
                            <p
                              className="italic m-0"
                              style={{ fontSize: "0.8rem" }}
                            >
                              {group.allowMultiple
                                ? t(
                                    `${tKey}variantsManagement.multipleChoiceInfo`,
                                  )
                                : t(
                                    `${tKey}variantsManagement.singleChoiceInfo`,
                                  )}
                            </p>
                          </Alert>

                          {/* Variants list */}
                          {variants.length === 0 ? (
                            <p className="text-medium italic p-2">
                              {t(`${tKey}variantsManagement.noVariants`)}
                            </p>
                          ) : (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) =>
                                handleDragEndVariants(
                                  event,
                                  group.dishVariantGroupId,
                                )
                              }
                            >
                              <SortableContext
                                items={variantIds}
                                strategy={verticalListSortingStrategy}
                              >
                                <div className="flex column">
                                  {variants.map((variant) => (
                                    <VariantRow
                                      key={variant.dishVariantId}
                                      variant={variant}
                                      tKey={tKey}
                                      editVariantId={editVariantId}
                                      editVariantData={editVariantData}
                                      setEditVariantId={setEditVariantId}
                                      setEditVariantData={setEditVariantData}
                                      deleteConfirmVariantId={
                                        deleteConfirmVariantId
                                      }
                                      setDeleteConfirmVariantId={
                                        setDeleteConfirmVariantId
                                      }
                                      onUpdate={handleUpdateVariant}
                                      onDelete={handleDeleteVariant}
                                    />
                                  ))}
                                </div>
                              </SortableContext>
                            </DndContext>
                          )}

                          {/* Add variant form */}
                          <div
                            className="flex gap-1 p-3 align-center"
                            style={{ borderTop: "1px solid #eee" }}
                          >
                            <input
                              type="text"
                              value={newVForm.label}
                              onChange={(e) =>
                                updateNewVariantForm(
                                  group.dishVariantGroupId,
                                  "label",
                                  e.target.value,
                                )
                              }
                              className="bg-pastel-light"
                              style={{ flex: 3 }}
                              placeholder={t(
                                `${tKey}variantsManagement.variantLabel`,
                              )}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  handleAddVariant(group.dishVariantGroupId);
                              }}
                            />
                            <input
                              type="number"
                              step="0.05"
                              value={newVForm.priceImpact}
                              onChange={(e) =>
                                updateNewVariantForm(
                                  group.dishVariantGroupId,
                                  "priceImpact",
                                  e.target.value,
                                )
                              }
                              className="bg-pastel-light"
                              style={{ flex: 1, minWidth: "80px" }}
                              placeholder={t(
                                `${tKey}variantsManagement.priceImpact`,
                              )}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  handleAddVariant(group.dishVariantGroupId);
                              }}
                            />
                            <Button
                              icon
                              variant="primary"
                              onClick={() =>
                                handleAddVariant(group.dishVariantGroupId)
                              }
                              disabled={!newVForm.label?.trim()}
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </Button>
                          </div>
                        </Collapse>
                      </div>
                    )}
                  </SortableRow>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </>
  );
};

export default VariantGroupManager;
